import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleFilled,
  PrinterOutlined,
} from '@ant-design/icons';
import { EmailButton } from '@components';
import { Inspection } from '@services';
import { Button, Col, Popconfirm, Progress, Row, Tooltip, Typography } from 'antd';

const { Text } = Typography;

/**
 * How far along the checklist is, without opening it.
 *
 * The decision taken on this row is whether to publish or send, and a half-filled checklist is
 * exactly what should not go to a customer. The bar answers "barely started or nearly there" at a
 * glance; the count is there for deciding whether to go and ask the technician. Complete turns green
 * because that is the one state in which the buttons beside it are safe to press.
 */
const InspectionProgress: React.FC<{ answered: number; total: number }> = ({ answered, total }) => {
  // No checklist items means nothing to be part-way through - an older inspection whose category has
  // since been emptied, say. A bar reading 0% would be a claim, not a fact.
  if (!total) return null;

  const complete = answered >= total;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ width: 64 }}>
        <Progress
          percent={Math.round((answered / total) * 100)}
          showInfo={false}
          size="small"
          strokeColor={complete ? '#52c41a' : '#1677ff'}
          style={{ margin: 0 }}
        />
      </div>
      <Text type={complete ? 'success' : 'secondary'} style={{ fontSize: 12 }}>
        {answered}/{total}
      </Text>
    </div>
  );
};

/**
 * Publishing sends the checklist to the customer and raises an internal request off the back of it,
 * so it is worth a word when the checklist is not finished. An unanswered item is not "fine" - it is
 * one nobody looked at - and the customer cannot tell the difference once it lands.
 */
const PublishConfirm: React.FC<{ inspection: Inspection; onConfirm: () => void }> = ({
  inspection,
  onConfirm,
}) => {
  const { answeredCount, itemCount, isPublished } = inspection;
  // No item count means nothing to be short of, so there is nothing to warn about.
  const incomplete = itemCount > 0 && answeredCount < itemCount;

  return (
    <Popconfirm
      title={incomplete ? 'This checklist is not finished' : 'Publish this inspection?'}
      description={
        incomplete
          ? `${answeredCount} of ${itemCount} items answered. The rest will reach the customer with no verdict against them.`
          : undefined
      }
      icon={incomplete ? <ExclamationCircleFilled style={{ color: '#faad14' }} /> : undefined}
      okText={incomplete ? 'Publish anyway' : 'Publish'}
      cancelText={incomplete ? 'Go back' : 'Cancel'}
      onConfirm={onConfirm}
      disabled={isPublished}
    >
      <Button size="small" type="primary" disabled={isPublished}>
        {isPublished ? 'Published' : 'Publish'}
      </Button>
    </Popconfirm>
  );
};

interface IProps {
  handleEdit: (id: string) => void;
  handlePublish: (id: string) => void;
  handleVhcEmailOpen: (id: string) => void;
  handlePrint: (id: string) => void;
  /** Takes the inspection off the job by category. Left out where the table is read-only. */
  handleRemove?: (categoryId: number) => void;
}

export const Columns = ({
  handleEdit,
  handlePublish,
  handleVhcEmailOpen,
  handlePrint,
  handleRemove,
}: IProps) => [
  // No titles: with one name and one row of buttons per line the headers said nothing, on the
  // desktop table or as labels on the mobile cards. The header row itself is hidden in CSS.
  {
    key: 'name',
    ellipsis: true,
    render: (inspection: Inspection) => (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <Text ellipsis={{ tooltip: inspection.category.name }}>{inspection.category.name}</Text>
        <InspectionProgress answered={inspection.answeredCount} total={inspection.itemCount} />
      </div>
    ),
  },
  {
    key: 'action',
    align: 'right',
    render: (inspection: Inspection) => (
      <Row gutter={8} justify="end">
        <Col>
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(inspection.id)}>
            Edit
          </Button>
        </Col>
        <Col>
          <PublishConfirm inspection={inspection} onConfirm={() => handlePublish(inspection.id)} />
        </Col>
        <Col>
          <EmailButton size="small" onClick={() => handleVhcEmailOpen(inspection.id)} />
        </Col>
        <Col>
          <Button
            size="small"
            type="primary"
            icon={<PrinterOutlined />}
            onClick={() => handlePrint(inspection.id)}
          >
            Print
          </Button>
        </Col>
        {handleRemove && (
          <Col>
            {/* Published means the customer has it and a request was raised from it - that is not
                undone by taking the row off the job, so it stays. */}
            <Popconfirm
              title="Remove this inspection from the job?"
              okText="Remove"
              okButtonProps={{ danger: true }}
              cancelText="Keep"
              onConfirm={() => handleRemove(inspection.category.id)}
              disabled={inspection.isPublished}
            >
              {/* Tooltip directly on the button: a disabled button fires no mouse events, and only
                  Tooltip knows to wrap it so the hint still shows. */}
              <Tooltip
                title={
                  inspection.isPublished ? 'A published inspection cannot be removed' : undefined
                }
              >
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  disabled={inspection.isPublished}
                >
                  Remove
                </Button>
              </Tooltip>
            </Popconfirm>
          </Col>
        )}
      </Row>
    ),
  },
];
